import { api } from '../api';
import type { FinalizedScoreDetail } from './practiceApi';
import { unwrapList, unwrapNpcLevels, unwrapNpcProgressSummary, unwrapNpcSession } from './npcResponses';

// Types
export type DifficultyTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'master';

export interface NpcPersona {
  id: string;
  key: string;
  label: string;
  description: string;
  domainKey: string;
  scenarioKey?: string;
  difficultyTier: DifficultyTier;
  styleTags: string[];
  avatarUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PersonaWithLevelSummary extends NpcPersona {
  totalLevels: number;
  passedLevels: number;
  nextLevelNumber?: number;
}

export interface RecommendedPersona extends PersonaWithLevelSummary {
  recommendationReason: string;
}

export interface NpcLevelDefinition {
  id: string;
  personaId: string;
  levelKey: string;
  levelNumber: number;
  displayName: string;
  description?: string;
  minOverallScore: number;
  minClarityScore?: number;
  minConfidenceScore?: number;
  minPaceScore?: number;
  minEnergyScore?: number;
  minSessions: number;
  rewardXp: number;
  createdAt: string;
  updatedAt: string;
}

export interface NpcLevelProgress {
  id: string;
  userId: string;
  levelId: string;
  bestOverallScore: number;
  bestClarityScore?: number;
  bestConfidenceScore?: number;
  bestPaceScore?: number;
  bestEnergyScore?: number;
  attempts: number;
  passed: boolean;
  passedAt?: string;
  lastAttemptAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** The user's progress on a level, as the levels endpoint reports it. */
export interface LevelProgressSummary {
  /** The best MEASURED overall; null when no attempt was measured (never a 0). */
  bestOverallScore: number | null;
  attempts: number;
  passed: boolean;
  passedAt?: string;
  lastAttemptAt?: string;
}

/**
 * A level with the user's progress — normalized from the backend's flat level
 * (label, isPassed, attempts, ...) by unwrapNpcLevels (npcResponses.ts).
 */
export interface LevelWithProgress extends Omit<NpcLevelDefinition, 'createdAt' | 'updatedAt'> {
  /** Absent until the user has attempted the level. */
  progress?: LevelProgressSummary;
  isLocked: boolean;
}

export interface PersonaProgress {
  personaId: string;
  totalLevels: number;
  passedLevels: number;
  totalXpEarned: number;
  levels: LevelWithProgress[];
}

/**
 * GET /npc/progress/summary, as the server returns it. Persona mastery (all of
 * a persona's levels passed) is not in it: it comes from the persona list's
 * per-persona totalLevels/passedLevels.
 */
export interface NpcProgressSummary {
  totalPersonasAttempted: number;
  totalLevelsPassed: number;
  totalXpEarned: number;
}

export interface DifficultyTierInfo {
  label: string;
  color: string;
  icon: string;
}

export interface NpcObjectionPattern {
  id: string;
  personaId: string;
  name: string;
  description?: string;
  maxSteps: number;
  isDefault: boolean;
  steps?: NpcObjectionStep[];
}

export interface NpcObjectionStep {
  id: string;
  patternId: string;
  stepIndex: number;
  triggerType: 'opening' | 'price_discussion' | 'commitment' | 'closing' | 'random';
  objectionTextTemplate: string;
  intensityLevel: number;
  followUpHint?: string;
}

export interface CreateNpcSessionRequest {
  personaId: string;
  levelId?: string;
}

export interface NpcSession {
  id: string;
  userId: string;
  npcPersonaId: string;
  npcLevelId?: string;
  type: 'npc_practice';
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  startedAt: string;
  endedAt?: string;
  durationSeconds: number;
  scores: {
    clarity: number;
    pace: number;
    filler_words: number;
    confidence: number;
    energy: number;
    overall: number;
  };
}

export interface FinalizeNpcSessionRequest {
  transcript?: string;
  audioBase64?: string;
  durationSeconds: number;
}

export interface FinalizeNpcSessionResponse {
  /** The completed row, without its score blob (carried once, as `scores`). */
  session: Omit<NpcSession, 'scores'>;
  /** Dimension detail only — no legacy overall (see FinalizedScoreDetail). */
  scores: FinalizedScoreDetail;
  /** THIS attempt's canonical overall (= npc.levelResult.attemptScore when a level was judged). */
  attemptScore?: number | null;
  evidenceModality?: 'audio' | 'text';
  feedback: {
    summary: string;
    strengths: string[];
    improvements: string[];
    tips: string[];
  };
  analysis: {
    wordCount: number;
    fillerWordCount: number;
    /** null when the pace could not be measured: render "not measured", never 0. */
    wordsPerMinute: number | null;
    fillerWords: string[];
  };
  npc?: {
    persona?: {
      id: string;
      label: string;
      difficultyTier: DifficultyTier;
    };
    level?: {
      id: string;
      label: string;
      levelNumber: number;
      minOverallScore: number;
    };
    levelResult?: {
      passed: boolean;
      newlyPassed: boolean;
      xpAwarded: number;
      /** null for an attempt that was not evaluable (nothing measured): "Not evaluable", never 0%. */
      currentBestScore: number | null;
      /**
       * THIS attempt's canonical evidence overall — the value the level
       * authority judged. null = not measured. The response carries no
       * legacy heuristic overall (it never decided the level).
       */
      attemptScore?: number | null;
      /** null when the level definition could not be read (not-evaluable view). */
      requiredScore: number | null;
      /** Present when nothing could be measured: the attempt neither passed nor failed. */
      notEvaluable?: true;
      scoresMet: {
        overall: boolean;
        clarity: boolean;
        confidence: boolean;
        connection: boolean;
        pace: boolean;
      };
    };
    nextLevelUnlocked?: {
      id: string;
      label: string;
      levelNumber: number;
    };
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PersonaFilters {
  domainKey?: string;
  scenarioKey?: string;
  difficultyTier?: DifficultyTier;
}

export const npcApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all NPC personas with optional filters
    getNpcPersonas: builder.query<PersonaWithLevelSummary[], PersonaFilters | void>({
      // includeProgress: the server adds totalLevels/passedLevels/nextLevelNumber
      // (PersonaWithLevelSummary) only when asked. Without it every persona
      // card read " / " levels and 0%, and nobody was ever "Mastered".
      query: (filters) => ({
        url: '/npc/personas',
        params: { ...(filters || {}), includeProgress: 'true' },
      }),
      transformResponse: (response: ApiResponse<PersonaWithLevelSummary[]>) =>
        unwrapList<PersonaWithLevelSummary>(response),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'NpcPersonas' as const, id })),
              { type: 'NpcPersonas', id: 'LIST' },
            ]
          : [{ type: 'NpcPersonas', id: 'LIST' }],
    }),

    // Get levels for a persona
    getNpcLevels: builder.query<LevelWithProgress[], string>({
      query: (personaId) => `/npc/personas/${personaId}/levels`,
      // The backend answers { persona, levels } — unwrap `levels`, never the object.
      transformResponse: (response: unknown) => unwrapNpcLevels(response),
      providesTags: (_result, _error, personaId) => [
        { type: 'NpcLevels', id: personaId },
        'NpcProgress',
      ],
    }),

    // Get overall progress summary
    getNpcProgressSummary: builder.query<NpcProgressSummary | null, void>({
      query: () => '/npc/progress/summary',
      transformResponse: (response: unknown) => unwrapNpcProgressSummary(response),
      providesTags: ['NpcProgress'],
    }),

    // Create an NPC practice session
    createNpcSession: builder.mutation<NpcSession, CreateNpcSessionRequest>({
      query: (body) => ({
        url: '/practice/npc-session',
        method: 'POST',
        body,
      }),
      // 201 data is { session, persona, level, objectionPattern }.
      transformResponse: (response: unknown) => unwrapNpcSession(response),
      invalidatesTags: ['Sessions'],
    }),

    // Finalize an NPC practice session
    finalizeNpcSession: builder.mutation<
      FinalizeNpcSessionResponse,
      { id: string; data: FinalizeNpcSessionRequest }
    >({
      query: ({ id, data }) => ({
        url: `/practice/sessions/${id}/finalize-npc`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<FinalizeNpcSessionResponse>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        'Sessions',
        { type: 'Sessions', id },
        'NpcProgress',
        'Progress',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNpcPersonasQuery,
  useGetNpcLevelsQuery,
  useGetNpcProgressSummaryQuery,
  useCreateNpcSessionMutation,
  useFinalizeNpcSessionMutation,
} = npcApi;
